import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { colors } from '../theme/colors';

/**
 * Tick-box dropdown for picking several options (2026-10-12, first used for
 * Item Sale Analysis → Category). Nothing ticked = "All".
 *
 *   options   [{ value, label }]
 *   selected  array of values
 *   onChange  (nextArray) => void
 *   allLabel  text shown when nothing is ticked (e.g. "All Categories")
 *   noun      plural word for the count text (e.g. "categories")
 */
const MultiCheckSelect = ({ options, selected, onChange, allLabel = 'All', noun = 'selected', searchPlaceholder = 'Search...' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const chosen = useMemo(() => new Set(selected || []), [selected]);

  useEffect(() => { if (!open) setSearch(''); }, [open]); // fresh search each time it opens
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter(o => String(o.label).toLowerCase().includes(q)) : options;
  }, [options, search]);

  const toggle = (v) => {
    const next = new Set(chosen);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(options.map(o => o.value).filter(x => next.has(x)));
  };
  const selectShown = () => {
    const next = new Set(chosen);
    shown.forEach(o => next.add(o.value));
    onChange(options.map(o => o.value).filter(x => next.has(x)));
  };

  const pickedLabels = options.filter(o => chosen.has(o.value)).map(o => o.shortLabel || o.label);
  const text = pickedLabels.length === 0 ? allLabel
    : pickedLabels.length <= 2 ? pickedLabels.join(', ')
    : `${pickedLabels.length} ${noun}`;

  return (
    <Wrap ref={wrapRef}>
      <Trigger type="button" onClick={() => setOpen(o => !o)} $active={pickedLabels.length > 0} title={pickedLabels.join(', ') || allLabel}>
        <span>{text}</span>
        <FiChevronDown size={14} />
      </Trigger>
      {open && (
        <Panel>
          <SearchRow>
            <FiSearch size={13} />
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder={searchPlaceholder} />
          </SearchRow>
          <Actions>
            <button type="button" onClick={selectShown}>Select All</button>
            <button type="button" onClick={() => onChange([])}>Clear</button>
          </Actions>
          <List>
            {shown.length === 0 && <Empty>No match</Empty>}
            {shown.map(o => (
              <Option key={o.value} data-value={o.value}>
                <input type="checkbox" checked={chosen.has(o.value)} onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
              </Option>
            ))}
          </List>
        </Panel>
      )}
    </Wrap>
  );
};

const Wrap = styled.div`position: relative; min-width: 0;`;
const Trigger = styled.button`
  width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 9px 12px; font-family: inherit; font-size: 13px; text-align: left;
  color: ${colors.text.primary}; background: white; cursor: pointer;
  border: 1px solid ${p => (p.$active ? colors.primary.main : colors.neutral[300])};
  border-radius: ${colors.borderRadius.md};
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const Panel = styled.div`
  position: absolute; z-index: 50; top: calc(100% + 4px); left: 0; right: 0; min-width: 240px;
  background: white; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md};
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
`;
const SearchRow = styled.div`
  display: flex; align-items: center; gap: 6px; padding: 8px 10px; border-bottom: 1px solid ${colors.neutral[200]};
  color: ${colors.text.secondary};
  input { flex: 1; border: none; outline: none; font-size: 13px; font-family: inherit; min-width: 0; }
`;
const Actions = styled.div`
  display: flex; justify-content: space-between; padding: 6px 10px; border-bottom: 1px solid ${colors.neutral[200]};
  button { border: none; background: none; color: ${colors.primary.main}; font-size: 12px; font-weight: 600; cursor: pointer; padding: 2px 0; }
`;
const List = styled.div`max-height: 280px; overflow-y: auto; padding: 4px 0;`;
const Option = styled.label`
  display: flex; align-items: center; gap: 8px; padding: 6px 12px; font-size: 13px; cursor: pointer;
  &:hover { background: ${colors.neutral[100]}; }
  input { cursor: pointer; }
`;
const Empty = styled.div`padding: 10px 12px; font-size: 12px; color: ${colors.text.secondary};`;

export default MultiCheckSelect;
